import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { CheckIcon, CloseIcon, ErrorIcon, WarningIcon } from '@/pages/fulfillment/components/UltimateIcons';
import type { FulfillmentToastItem, FulfillmentToastTone } from '@/pages/fulfillment/components/useFulfillmentToast';
import { FULFILLMENT_TOAST_MS } from '@/pages/fulfillment/components/toastTiming';
import '@/pages/fulfillment/components/fulfillmentToast.css';

/** Re-exported for the screens and tests that read the clock from the component. */
export { FULFILLMENT_TOAST_MS };

/** What the stack shows at once; anything older scrolls inside the panel, and nothing is dropped to make room. */
export const FULFILLMENT_TOAST_STACK_SIZE = 3;

/** Half of it inside the panel is the least an operator can read. */
const READABLE_RATIO = 0.5;
/**
 * Steps of 1 % so the observer reports a tall card while it scrolls into view: with only 0 / 50 / 100 % a
 * card ten times the panel would never report the moment it fills half the panel (SSD-7705).
 */
const VISIBILITY_STEPS = Array.from({ length: 101 }, (_, step) => step / 100);

export interface FulfillmentToastHostProps {
  toasts: FulfillmentToastItem[];
  onDismiss: (id: number) => void;
  onExpire?: (id: number) => void;
}

/**
 * Whether the operator can see this notification in the stack. One scrolled out of the panel is not
 * being read, so its clock does not run: a card must not expire behind the ones standing over it. A card
 * taller than the panel counts once it fills half the panel, or a long refusal list would never leave.
 */
const useReadable = (target: RefObject<Element>, root: RefObject<Element>): boolean => {
  // Where nothing can observe it (jsdom, an old browser), a toast that expires beats one that never does.
  const observable = typeof IntersectionObserver !== 'undefined';
  const [readable, setReadable] = useState(!observable);

  useEffect(() => {
    const element = target.current;
    if (!observable || !element) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = entries[entries.length - 1];
        if (latest) {
          const panel = latest.rootBounds?.height ?? 0;
          const fillsPanel = panel > 0 && (latest.intersectionRect?.height ?? 0) >= panel * READABLE_RATIO;
          setReadable(latest.isIntersecting && (latest.intersectionRatio >= READABLE_RATIO || fillsPanel));
        }
      },
      { root: root.current, threshold: VISIBILITY_STEPS },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [observable, root, target]);

  return readable;
};

interface ToastProps {
  toast: FulfillmentToastItem;
  /** How long it stays once it can be read; none for a card that waits to be closed. */
  lifetimeMs: number | null;
  listRef: RefObject<HTMLDivElement>;
  paused: boolean;
  onDismiss: (id: number) => void;
  onExpire: (id: number) => void;
}

/** The mark beside the words, so the tone reads before the sentence does. */
const TONE_ICON: Record<FulfillmentToastTone, typeof CheckIcon> = {
  success: CheckIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

const Toast = ({ toast, lifetimeMs, listRef, paused, onDismiss, onExpire }: ToastProps) => {
  const { id } = toast;
  const ref = useRef<HTMLDivElement>(null);
  const readable = useReadable(ref, listRef);
  // A screen may hand over a new callback on every render, and that must not restart a clock already running.
  const expireRef = useRef(onExpire);
  useEffect(() => {
    expireRef.current = onExpire;
  });

  const remainingMs = useRef(lifetimeMs);
  const running = lifetimeMs !== null && readable && !paused;
  useEffect(() => {
    if (!running || remainingMs.current === null) {
      return undefined;
    }
    const startedAt = Date.now();
    const timer = setTimeout(() => expireRef.current(id), remainingMs.current);
    return () => {
      clearTimeout(timer);
      if (remainingMs.current !== null) {
        remainingMs.current = Math.max(0, remainingMs.current - (Date.now() - startedAt));
      }
    };
  }, [id, running]);

  const Icon = TONE_ICON[toast.tone];
  return (
    <div
      ref={ref}
      className={`ff-toast ff-toast-${toast.tone}`}
      data-testid="action-toast"
      data-dismissal={lifetimeMs === null ? 'manual' : 'auto'}
      role={toast.tone === 'error' ? 'alert' : 'status'}
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
    >
      <span className="ff-toast-mark">
        <Icon />
      </span>
      <div className="ff-toast-body">
        <div className="ff-toast-text">{toast.text}</div>
        {toast.details && toast.details.length > 0 && (
          <ul className="ff-toast-details">
            {toast.details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
        )}
        {toast.action && (
          <button
            type="button"
            className="ff-toast-close"
            onClick={() => {
              toast.action?.run();
              onDismiss(id);
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button type="button" className="ff-toast-x" aria-label={`Dismiss ${toast.text}`} onClick={() => onDismiss(id)}>
        <CloseIcon />
      </button>
      {lifetimeMs !== null && (
        // The time it has left, drawn so the desk can tell a card that will go from one that waits for them.
        <span
          className={`ff-toast-timer${running ? '' : ' is-paused'}`}
          style={{ animationDuration: `${lifetimeMs}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * The one feedback element every screen renders: a stack, oldest at the top and newest against the
 * corner. Each notification keeps its own clock — a card the desk can miss clears itself, one that says
 * something did not happen stays until it is closed — so an outcome that follows another never rewrites or
 * shortens the one before it.
 */
export const FulfillmentToastHost = ({ toasts, onDismiss, onExpire = onDismiss }: FulfillmentToastHostProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const overflowing = toasts.length > FULFILLMENT_TOAST_STACK_SIZE;
  const newestId = toasts[toasts.length - 1]?.id;

  // "Show all" answered the overflow that was standing; the next one starts collapsed again.
  useEffect(() => {
    if (!overflowing) {
      setExpanded(false);
    }
  }, [overflowing]);

  const pointerInside = useRef(false);
  const [paused, setPaused] = useState(false);

  // A card removed under the pointer may not send mouseleave. Recheck after the stack changes.
  useLayoutEffect(() => {
    const host = hostRef.current;
    setPaused(
      Boolean(host && ((pointerInside.current && host.matches(':hover')) || host.contains(document.activeElement))),
    );
  }, [toasts]);

  /** The panel is as tall as its newest few notifications, whatever their height; the rest scroll inside it. */
  const fitStack = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const first = list.children[list.children.length - FULFILLMENT_TOAST_STACK_SIZE] as HTMLElement | undefined;
    const last = list.lastElementChild as HTMLElement | null;
    if (expanded || list.children.length <= FULFILLMENT_TOAST_STACK_SIZE || !first || !last) {
      list.style.maxHeight = '';
      return;
    }
    const span = last.offsetTop + last.offsetHeight - first.offsetTop;
    const style = getComputedStyle(list);
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    // Nothing measurable (a print sheet hiding the page) must not collapse the panel to nothing.
    list.style.maxHeight = span > 0 ? `${span + padding}px` : '';
  }, [expanded]);

  useLayoutEffect(() => {
    fitStack();
  }, [fitStack, toasts]);

  // The outcome of what the operator just did is the one that has to be in view.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [newestId, expanded]);

  // A narrower window wraps the text, and the newest few are then a different height.
  useEffect(() => {
    window.addEventListener('resize', fitStack);
    return () => window.removeEventListener('resize', fitStack);
  }, [fitStack]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      ref={hostRef}
      className="ff-toast-host"
      role="region"
      aria-label="Notifications"
      onMouseEnter={() => {
        pointerInside.current = true;
        setPaused(true);
      }}
      onMouseLeave={() => {
        pointerInside.current = false;
        setPaused(Boolean(hostRef.current?.contains(document.activeElement)));
      }}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(pointerInside.current && event.currentTarget.matches(':hover'));
        }
      }}
    >
      {toasts.length > 1 && (
        <div className="ff-toast-bar">
          <span className="ff-toast-count">{toasts.length} notifications</span>
          {overflowing && (
            <button
              type="button"
              className="ff-toast-bar-btn"
              aria-label={expanded ? 'Show fewer notifications' : 'Show all notifications'}
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded ? 'Show fewer' : 'Show all'}
            </button>
          )}
          <button
            type="button"
            className="ff-toast-bar-btn ff-toast-clear"
            aria-label="Clear all notifications"
            onClick={() => toasts.forEach((toast) => onDismiss(toast.id))}
          >
            Clear all
          </button>
        </div>
      )}
      <div ref={listRef} className="ff-toast-list" tabIndex={0} aria-label="Notification list">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            lifetimeMs={toast.dismissal === 'auto' ? FULFILLMENT_TOAST_MS : null}
            listRef={listRef}
            paused={paused}
            onDismiss={onDismiss}
            onExpire={onExpire}
          />
        ))}
      </div>
    </div>
  );
};
